import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { ConversationEntry, EntryFeedback, updateEntryFeedback } from '../store/conversationSlice';
import { ConversationCategory } from '../types/conversation';
import FeedbackPopover from './FeedbackPopover';
import { apiService } from '../services/apiService';
import { sessionManager } from '../services/sessionManager';
import { useToast } from '../contexts/ToastContext';

const formatTime = (timestamp: string): string => {
  try {
    let ts = timestamp;
    if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
      ts = `${timestamp}Z`;
    }
    return new Date(ts).toLocaleTimeString(undefined, {
      hour12: true,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '';
  }
};

const formatAnswer = (answer: string): React.ReactNode => {
  const lines = answer.split('\n');
  return (
    <div>
      {lines.map((line, index) => {
        if (line.trim().startsWith('•') || line.trim().startsWith('-') || line.trim().startsWith('*')) {
          return (
            <li key={index} className="ml-4 mb-0.5 text-xs text-gray-800 list-disc">
              {line.trim().substring(1).trim()}
            </li>
          );
        }
        if (line.trim()) {
          return (
            <p key={index} className="mb-1 text-xs text-gray-800">
              {line.trim()}
            </p>
          );
        }
        return null;
      }).filter(Boolean)}
    </div>
  );
};

const STATUS_DOT_COLOR: Record<string, string> = {
  completed: 'bg-green-500',
  pending: 'bg-orange-500',
  error: 'bg-red-500',
};

interface EntryCardProps {
  entry: ConversationEntry;
}

const InquiryCard: React.FC<EntryCardProps> = ({ entry }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const isPending = entry.status === 'pending';

  const handleFeedbackSubmit = async (rating: 'up' | 'down', reason: string) => {
    const roomId = sessionManager.getRoomId();

    const feedback: EntryFeedback = {
      rating,
      reason: reason || undefined,
      submittedAt: new Date().toISOString(),
    };
    dispatch(updateEntryFeedback({ id: entry.id, feedback }));
    showToast('Feedback recorded');

    if (!roomId) return;

    try {
      await apiService.submitEntryFeedback(roomId, {
        entryType: 'conversation',
        entryTimestamp: entry.timestamp,
        category: entry.category,
        rating,
        reason,
      });
    } catch (err) {
      console.warn('[NewInquiryBar] Entry feedback persist failed:', err);
    }
  };

  return (
    <div className="border-2 border-gray-300 rounded-xl overflow-hidden shadow-sm bg-white">
      {/* Card header */}
      <div className="flex items-center justify-between bg-gray-100 border-b-2 border-gray-300 px-3 py-1.5">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-purple-700 uppercase tracking-wide">New Inquiry</span>
          <span className="text-xs text-gray-500">{formatTime(entry.timestamp)}</span>
        </div>
        <div className="flex items-center gap-2">
          <FeedbackPopover
            currentFeedback={entry.feedback}
            onSubmit={handleFeedbackSubmit}
            disabled={isPending}
          />
          <div
            className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_DOT_COLOR[entry.status] ?? 'bg-gray-500'}`}
            title={entry.status}
          />
        </div>
      </div>

      {/* Question */}
      <div className="bg-purple-50 border-b-2 border-purple-200 px-3 py-2">
        <p className="text-xs font-bold text-black mb-0.5 uppercase tracking-wide">Customer asked</p>
        <p className="text-sm text-gray-900 font-medium leading-snug">{entry.question}</p>
      </div>

      {/* Answer section */}
      <div className="p-3">
        <p className="text-xs font-bold text-black uppercase tracking-wide mb-1.5">AI Generated Answer</p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 min-h-[40px]">
          {isPending && !entry.answer ? (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-1">
              <svg className="animate-spin w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Generating AI answer...
            </div>
          ) : entry.answer ? (
            formatAnswer(entry.answer)
          ) : (
            <p className="text-xs text-gray-400 italic">No AI answer available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export const NewInquiryBar: React.FC = () => {
  const { entries } = useSelector((state: RootState) => state.conversation);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);
  const feedRef = useRef<HTMLDivElement>(null);
  const seenCountRef = useRef(0);

  const newInquiries = entries.filter(
    (e) => e.category === ConversationCategory.NEW_INQUIRY
  );
  const latestInquiry = newInquiries.length > 0 ? newInquiries[newInquiries.length - 1] : null;

  // Badge flashes for 3 pulses (3 × 0.4s = 1.2s) then stays red until user opens.
  useEffect(() => {
    if (!isExpanded && newInquiries.length > seenCountRef.current) {
      setHasNew(true);
      setIsFlashing(true);
      const timer = setTimeout(() => setIsFlashing(false), 1200);
      return () => clearTimeout(timer);
    }
  }, [newInquiries.length, isExpanded]);

  useEffect(() => {
    if (isExpanded && feedRef.current) {
      requestAnimationFrame(() => {
        if (feedRef.current) {
          feedRef.current.scrollTop = feedRef.current.scrollHeight;
        }
      });
    }
  }, [newInquiries.length, isExpanded]);

  const handleToggle = () => {
    setIsExpanded((prev) => {
      const opening = !prev;
      if (opening) {
        seenCountRef.current = newInquiries.length;
        setHasNew(false);
      }
      return opening;
    });
  };

  return (
    <>
      {/* Collapsed bar */}
      <div
        className={`px-4 py-1 cursor-pointer transition-colors ${
          !isExpanded && hasNew
            ? 'bg-purple-100 hover:bg-purple-200'
            : 'hover:bg-gray-50'
        }`}
        onClick={handleToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 min-w-0">
            <div className="flex items-center space-x-1 flex-shrink-0">
              <span className="text-sm">💡</span>
              <h3 className={`text-xs font-semibold ${
                !isExpanded && hasNew ? 'text-purple-900' : 'text-gray-800'
              }`}>
                New Inquiries
              </h3>
              <span className={`px-1.5 py-0 text-xs rounded-full border font-medium ${
                !isExpanded && hasNew
                  ? `bg-red-500 text-white border-red-600${isFlashing ? ' animate-badge-flash' : ''}`
                  : newInquiries.length > 0
                  ? 'bg-purple-100 text-purple-800 border-purple-300'
                  : 'bg-gray-100 text-gray-600 border-gray-300'
              }`}>
                {newInquiries.length}
              </span>
            </div>

            {latestInquiry ? (
              <div className="flex items-center space-x-1 text-xs min-w-0">
                <span className="flex-shrink-0 text-gray-400">Latest:</span>
                <span className={`truncate ${
                  !isExpanded && hasNew ? 'text-purple-900 font-medium' : 'text-gray-600'
                }`}>
                  {latestInquiry.question.length > 60
                    ? `${latestInquiry.question.substring(0, 60)}\u2026`
                    : latestInquiry.question}
                </span>
              </div>
            ) : (
              <span className="text-xs text-gray-400">No new inquiries yet</span>
            )}
          </div>

          <button
            className={`transition-colors p-0.5 flex-shrink-0 ml-2 ${
              !isExpanded && hasNew
                ? 'text-purple-600 hover:text-purple-900'
                : 'text-gray-400 hover:text-gray-600'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
          >
            <svg
              className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Expanded dropdown — contained within this bar's own relative column */}
      {isExpanded && (
        <div className="absolute left-0 right-0 top-full border-t border-gray-200 bg-gray-50 shadow-lg z-50">
          <div
            ref={feedRef}
            className="px-3 py-3 max-h-[50vh] overflow-y-auto"
            style={{ scrollBehavior: 'smooth' }}
          >
            {newInquiries.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <div className="text-4xl mb-2">💡</div>
                <p className="text-sm font-medium">No new inquiries yet</p>
                <p className="text-xs text-gray-400 mt-1">Unrecognized questions will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {newInquiries.map((entry) => (
                  <InquiryCard key={entry.id} entry={entry} />
                ))}
                <div className="h-1" />
              </div>
            )}
          </div>

          {newInquiries.length > 0 && (
            <div className="px-4 py-2 border-t-2 border-gray-200 bg-white text-center">
              <span className="text-xs font-medium text-gray-600">
                {newInquiries.length} new inquir{newInquiries.length === 1 ? 'y' : 'ies'} detected
              </span>
            </div>
          )}
        </div>
      )}
    </>
  );
};
